// Taps: the bar under the stage (a count of taps, Undo tap, Review) and the Review sheet (one row per tap, Replay, Keep or Remove).
// DESIGN §9. The bar sits in the player's own column and never over the stage: it cannot push the numeral, which is above it.

import type { TapRow } from '../engine/engine.js';
import { putBackTap, removeTap, replayFromSheet, reviewTaps, startOver, stopReplay, tapWords, undoTap } from './actions.js';
import { Choice } from './controls.js';
import { useEngineState } from './engine-state.js';
import { Sheet } from './sheet.js';
import { closeSheet, ui } from './ui.js';

/** A time in the song for a row: 0:07.3. Tenths, because two taps can fall in the same second. */
export function tapTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
}

/** What a tap did, in words (DESIGN §9). */
export function effectWords(row: TapRow, oneSure = true): string {
  if (row.removed) return 'Removed';
  switch (row.effect) {
    case 'proposed': return oneSure ? 'Proposed 1 here' : 'Proposed 1 here, which may be four counts off';
    case 'set': return 'Set the count';
    case 'moved': return 'Moved 1 here';
    case 'agreed': return 'Agreed with the count';
    default: return 'Not used';
  }
}

/**
 * "3 taps" · Undo tap · Review, directly under the stage once there is a tap. Where the count is, a removal says "Tap removed · Put back" for a few
 * seconds (the button), and a replay says "Stop replay"; Undo tap and Review keep their places, so a quick second Undo tap never lands on Put back.
 * In truth mode the same Undo tap removes truth taps; there is nothing to review (the count is hidden), and in the lab there are no taps at all.
 */
export function TapsBar() {
  const s = useEngineState();
  const { removedNote } = ui.use();
  if (s.source !== 'file') return null;
  const live = s.truthMode ? s.truthTaps : s.tapRows.filter((r) => !r.removed && r.kind !== 'auto').length;
  const proposed = !s.truthMode && s.tapRows.some((r) => !r.removed && r.kind === 'auto');
  if (live === 0 && !proposed && !s.canPutBack) return null;
  // Where the count came from (DESIGN §11, 5): the proposal, the proposal and then a tap of the dancer's, or the taps.
  const origin = s.truthMode ? 'taps' : s.auto.origin;
  return (
    <div class="taps-bar chrome" role="group" aria-label="Taps">
      {s.replay ? (
        <button type="button" class="btn quiet taps-left" onClick={stopReplay}>Stop replay</button>
      ) : removedNote !== null && s.canPutBack ? (
        <button type="button" class="btn quiet taps-left" data-testid="put-back" onClick={() => putBackTap()}>
          Tap removed · <span class="taps-action">Put back</span>
        </button>
      ) : (
        <span class="taps-count" data-testid="taps-count">{origin === 'auto' ? 'Found automatically' : origin === 'overridden' ? 'Count set by you' : tapWords(live)}</span>
      )}
      <button type="button" class="btn" data-testid="undo-tap" disabled={!s.canUndoTap} onClick={undoTap}>Undo tap</button>
      {s.truthMode ? <span aria-hidden="true" /> : (
        <button type="button" class="btn" aria-label="Review taps" aria-haspopup="dialog" disabled={s.tapRows.length === 0} onClick={reviewTaps}>Review</button>
      )}
    </div>
  );
}

/** The proposal's row, always first (DESIGN §11, 5): when it was found, what it proposed, Keep or Remove like a tap. Remove returns to "Tap when you feel 1". It has no Replay: it is not a tap. */
function AutoRow({ row, oneSure }: { row: TapRow; oneSure: boolean }) {
  const name = 'the automatic count';
  return (
    <li class="tap-row" data-testid="auto-row" data-removed={row.removed ? '' : undefined} data-effect={row.removed ? 'removed' : 'proposed'}>
      <div class="tap-text">
        <span class="tap-title">Found automatically</span>
        {!row.removed && <span class="tap-time">{tapTime(row.t)}</span>}
        <span class="tap-effect">{effectWords(row, oneSure)}</span>
      </div>
      <div class="tap-actions">
        <Choice<'keep' | 'remove'>
          label={`Keep or remove ${name}`}
          value={row.removed ? 'remove' : 'keep'}
          options={[{ value: 'keep', label: 'Keep', name: `Keep ${name}` }, { value: 'remove', label: 'Remove', name: `Remove ${name}` }]}
          onChange={(v) => { if (v === 'keep' && row.removed) putBackTap(row.id); else if (v === 'remove' && !row.removed) removeTap(row.id); }}
        />
      </div>
    </li>
  );
}

function Row({ row }: { row: TapRow }) {
  const title = row.removed ? 'Removed tap' : `Tap ${row.n}`;
  const name = row.removed ? `the removed tap at ${tapTime(row.t)}` : `tap ${row.n}`;
  return (
    <li class="tap-row" data-testid="tap-row" data-removed={row.removed ? '' : undefined} data-effect={row.removed ? 'removed' : row.effect}>
      <div class="tap-text">
        <span class="tap-title">{title}</span>
        <span class="tap-time">{tapTime(row.t)}</span>
        <span class="tap-effect">{effectWords(row)}</span>
      </div>
      <div class="tap-actions">
        <button type="button" class="btn" aria-label={`Replay ${name}`} disabled={row.removed} onClick={() => replayFromSheet(row.id)}>Replay</button>
        <Choice<'keep' | 'remove'>
          label={`Keep or remove ${name}`}
          value={row.removed ? 'remove' : 'keep'}
          options={[{ value: 'keep', label: 'Keep', name: `Keep ${name}` }, { value: 'remove', label: 'Remove', name: `Remove ${name}` }]}
          onChange={(v) => { if (v === 'keep' && row.removed) putBackTap(row.id); else if (v === 'remove' && !row.removed) removeTap(row.id); }}
        />
      </div>
    </li>
  );
}

/** The Review sheet: Replay all at the top, one row per tap in song order, Start over at the bottom. */
export function ReviewSheet() {
  const s = useEngineState();
  const rows = s.tapRows;
  const live = rows.filter((r) => !r.removed && r.kind !== 'auto').length;
  return (
    <Sheet title="Review taps">
      {rows.length === 0 ? (
        <p class="note">No taps yet. Tap on 1 when you feel it.</p>
      ) : (
        <>
          <button type="button" class="btn primary" disabled={live === 0} onClick={() => replayFromSheet(null)}>Replay all</button>
          <ul class="tap-list" aria-label="Taps">
            {rows.map((r) => (r.kind === 'auto' ? <AutoRow key={`${r.id}:${r.removed}`} row={r} oneSure={s.auto.oneSure} /> : <Row key={r.id} row={r} />))}
          </ul>
          <p class="note">Replay plays 3 seconds before the tap and 3 after it, with a tick where you tapped. Remove takes a tap out of the count; Keep puts it back.</p>
        </>
      )}
      <button type="button" class="btn" data-testid="review-start-over" disabled={s.events.length === 0} onClick={() => { closeSheet(); startOver(); }}>Start over</button>
    </Sheet>
  );
}
